import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router'

import { LogoMark } from '@/components/layout/Logo'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { TwoFactorGate } from '@/features/platform/components/TwoFactorGate'

/**
 * Vist mens sessionen genoprettes ved appens opstart.
 *
 * Bevidst uden skelet af dashboardet: vi ved endnu ikke, om brugeren
 * overhovedet er logget ind, og et skelet ville antyde, at indholdet er på vej.
 */
function SessionLoading() {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-dvh flex-col items-center justify-center gap-3"
    >
      <LogoMark className="size-10 animate-shimmer" />
      <span className="sr-only">Genopretter din session</span>
    </div>
  )
}

/**
 * Kræver en gyldig session. Ikke-autentificerede brugere sendes til login med
 * den ønskede sti i `returnTo`, så de lander det rigtige sted bagefter.
 */
export function ProtectedRoute() {
  const status = useAuthStore((state) => state.status)
  const user = useAuthStore((state) => state.user)
  const location = useLocation()

  if (status === 'initialising') {
    return <SessionLoading />
  }

  if (status === 'anonymous') {
    const returnTo = `${location.pathname}${location.search}`

    return (
      <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />
    )
  }

  // Uden organisation kan appen ikke bruges: næsten alle endpoints kræver en.
  // Brugeren sendes derfor til onboarding frem for til en side, hvor alt fejler.
  if (user && user.organizations.length === 0) {
    return <Navigate to="/onboarding/organisation" replace />
  }

  return <Outlet />
}

/**
 * Platformadministrationen. Kun for leverandørens egne folk.
 *
 * Ligger inden i `ProtectedRoute`, så status allerede er afklaret her.
 * Kontrollen er ikke sikkerhed — hvert kald til /api/platform slår adgangen op
 * i databasen uafhængigt — men den forhindrer, at nogen lander på en side,
 * hvor alt svarer 403.
 *
 * De to afslag er forskellige med vilje: den, der ikke har adgangen, sendes
 * væk, mens den, der har den men mangler tofaktor, får at vide, hvad der
 * mangler. Det andet er et trin, brugeren selv kan tage.
 */
export function PlatformRoute() {
  const user = useAuthStore((state) => state.user)

  if (!user) {
    return null
  }

  if (!user.isPlatformAdmin) {
    return <Navigate to="/app/dashboard" replace />
  }

  return user.twoFactorEnabled ? <Outlet /> : <TwoFactorGate />
}

/**
 * Ruter der kun giver mening, når man <em>ikke</em> er logget ind (login,
 * registrering). En allerede logget ind bruger sendes til dashboardet.
 */
export function AnonymousOnlyRoute() {
  const status = useAuthStore((state) => state.status)
  const [searchParams] = useSearchParams()

  if (status === 'initialising') {
    return <SessionLoading />
  }

  if (status === 'authenticated') {
    // `returnTo` skal også respekteres her, ikke kun i LoginPage.
    //
    // Loginsiden navigerer selv videre efter et vellykket login, men det kald
    // taber kapløbet: så snart auth-storen skifter status, gengiver React
    // ruten, og denne komponent omdirigerer, før effekten i LoginPage når at
    // køre. Uden linjen nedenfor landede enhver, der fulgte et dybt link —
    // en godkendelsesanmodning eller en omtale fra en mail — på forsiden i
    // stedet for det, de blev bedt om at se på.
    const returnTo = searchParams.get('returnTo')

    // Kun stier under /app. Ellers kunne `?returnTo=//et-andet-sted.dk` gøre
    // vores egen loginside til et springbræt.
    return <Navigate to={returnTo?.startsWith('/app') ? returnTo : '/app/dashboard'} replace />
  }

  return <Outlet />
}

/**
 * Onboarding: kræver login, men ikke en organisation — det er netop det, der
 * skal oprettes. Har brugeren allerede en, er siden overflødig.
 */
export function OnboardingRoute() {
  const status = useAuthStore((state) => state.status)
  const user = useAuthStore((state) => state.user)

  if (status === 'initialising') {
    return <SessionLoading />
  }

  if (status === 'anonymous') {
    return <Navigate to="/login" replace />
  }

  if (user && user.organizations.length > 0) {
    return <Navigate to="/app/dashboard" replace />
  }

  return <Outlet />
}
